// src/Components/UserAuthComponents/ResetPassword.tsx
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

import "../../Styles/UserAuthStyles/Login.css";

// use the shared axios instance
import { api } from "../../api";

function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      await api.post(`/api/reset-password/${token}`, {
        token,
        password,
      });

      // If we reach here, the request succeeded (2xx)
      toast.success("Password reset successfully!");
      navigate("/login");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const message: string = err.response.data?.message ?? "";
        toast.error(message || "An unexpected error occurred. Please try again.");
      } else {
        console.error(err);
        toast.error("Network error while resetting password.");
      }
    }
  };

  return (
    <div className="login-shell">
      {/* subtle background overlay */}
      <div className="login-bg-overlay" />

      <div className="login-card">
        {/* Left hero pane */}
        <div className="login-hero">
          <h1 className="login-hero-title">
            Choose a new password.
          </h1>
          <p className="login-hero-subtitle">
            Pick something strong and memorable. Once reset, you can sign in
            right away with your new credentials.
          </p>

          <ul className="login-hero-list">
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Secure reset</strong> — this link expires after use.
              </span>
            </li>
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Instant access</strong> — sign in immediately after
                resetting.
              </span>
            </li>
          </ul>

          <div className="login-hero-footnote">
            Need help? Contact support at any time.
          </div>
        </div>

        {/* Right form pane */}
        <div className="login-form-pane">
          <header className="login-header">
            <h2 className="login-title">Reset your password</h2>
            <p className="login-subtitle">
              Enter a new password below to regain access to your account.
            </p>
          </header>

          <form className="login-form" onSubmit={handleResetPassword}>
            <label className="login-field">
              <span className="login-label">New password</span>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Password must be 8–20 characters
              </p>
            </label>

            <label className="login-field">
              <span className="login-label">Confirm password</span>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <div className="login-actions-row">
              <button
                type="button"
                className="login-link"
                onClick={() => navigate("/login")}
              >
                Back to sign in
              </button>

              <button type="submit" className="login-primary-button">
                <span className="login-primary-text">Reset password</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
